import { GameEvent, GameEventType } from 'modules/games/domain/interfaces/game-details';
import { ManualGoalPairing, ReviewClip, ReviewGoal, ReviewGoalMatch } from 'modules/match-video/domain/interfaces';

// Goals of the match itself. The post-match shootout has its own score and no clips.
const GOAL_TYPES = new Set<GameEventType>([
  GameEventType.GOAL,
  GameEventType.PASS_AND_GOAL,
  GameEventType.PENALTY,
  GameEventType.OWN_GOAL,
]);

const byMatchTime = (a: GameEvent, b: GameEvent) =>
  a.period - b.period || a.minutes - b.minutes || a.extraMinutes - b.extraMinutes || a.seconds - b.seconds || a.id - b.id;

function toReviewGoal(event: GameEvent): ReviewGoal {
  // PASS_AND_GOAL keeps `[passer, scorer]` (`game-details-mapper.ts`), every other goal — `[scorer]`.
  const isPassAndGoal = event.type === GameEventType.PASS_AND_GOAL;
  const [first, second] = event.playerIds;
  return {
    eventId: event.id,
    teamId: event.teamId,
    scorerId: (isPassAndGoal ? second : first) || undefined,
    assistId: (isPassAndGoal ? first : undefined) || undefined,
    period: event.period,
    minutes: event.minutes,
    extraMinutes: event.extraMinutes,
    isOwnGoal: event.type === GameEventType.OWN_GOAL,
    isPenalty: event.type === GameEventType.PENALTY,
  };
}

/** Protocol goals in match-time order. */
export function getReviewGoals(events: readonly GameEvent[]): ReviewGoal[] {
  return events
    .filter((event) => GOAL_TYPES.has(event.type))
    .sort(byMatchTime)
    .map(toReviewGoal);
}

/**
 * The n-th `goal` clip ↔ the n-th protocol goal. Camera time and match minutes are on different clocks, so order is
 * the only link; when the counts differ the pairing would shift every scorer, so nothing is paired automatically.
 * A choice by hand (`manual`) wins either way: one goal may go to several clips (a replay from another camera), and a
 * clip may be marked as having no protocol goal. A hand choice of a goal no longer in the protocol is ignored.
 * `clips` must already be in review order (`sortReviewClips`).
 */
export function matchGoalsToClips(
  events: readonly GameEvent[],
  clips: readonly ReviewClip[],
  manual: ManualGoalPairing = {},
): ReviewGoalMatch {
  const goals = getReviewGoals(events);
  const goalClips = clips.filter((clip) => clip.kind === 'goal');
  const mismatch = goals.length !== goalClips.length;
  const goalByEventId = new Map(goals.map((goal) => [goal.eventId, goal]));

  const goalByClipId: Record<string, ReviewGoal> = {};
  goalClips.forEach((clip, index) => {
    const chosen = Object.prototype.hasOwnProperty.call(manual, clip.id) ? manual[clip.id] : undefined;
    const byHand = chosen === null ? null : chosen === undefined ? undefined : goalByEventId.get(chosen);
    const goal = byHand === undefined ? (mismatch ? undefined : goals[index]) : byHand;
    if (goal) goalByClipId[clip.id] = goal;
  });

  return { goalByClipId, goals, protocolGoalCount: goals.length, goalClipCount: goalClips.length, mismatch };
}
